import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast, Toaster } from "sonner";
import { Loader2, ArrowRight, Shield } from "lucide-react";
import { detectDeviceType } from "../lib/device";

export default function Login() {
  const [stage, setStage] = useState("credentials");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const { login } = useAuth();
  const nav = useNavigate();

  // Show a friendly notice if we landed here from an inactivity logout
  useEffect(() => {
    try {
      const reason = sessionStorage.getItem("vs_logout_reason");
      if (reason === "session_expired") {
        toast.info("Session expired after 5 minutes of inactivity. Please sign in again.");
        sessionStorage.removeItem("vs_logout_reason");
      }
    } catch (err) { console.debug("logout-reason read skipped:", err); }
  }, []);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setInterval(() => setCooldown((c) => Math.max(0, c - 1)), 1000);
    return () => clearInterval(t);
  }, [cooldown]);

  const sendOtp = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/request-otp", { email, password });
      toast.success("OTP sent to your email. Check inbox.");
      setStage("otp");
      setCooldown(30);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Login failed");
    } finally { setLoading(false); }
  };

  const verify = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      // Send the detected device class so the backend can enforce per-user
      // device allowlists before issuing the JWT.
      const device_type = detectDeviceType();
      const r = await api.post("/auth/verify-otp", { email, code, device_type });
      login(r.data.token, r.data.user);
      toast.success(`Welcome, ${r.data.user.full_name}`);
      nav("/");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Invalid OTP");
    } finally { setLoading(false); }
  };

  const resend = async () => {
    if (cooldown > 0) return;
    setLoading(true);
    try {
      await api.post("/auth/request-otp", { email, password });
      toast.success("New OTP sent");
      setCooldown(30);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#FAF7F2] p-6"
      style={{
        backgroundImage: "radial-gradient(circle at 20% 10%, #F3ECDF 0%, transparent 40%), radial-gradient(circle at 80% 90%, #EFE3CE 0%, transparent 40%)",
      }}>
      <Toaster richColors position="top-right" />
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center gap-3 mb-6">
            <div className="w-14 h-14 bg-[#2D2622] text-[#FAF7F2] flex items-center justify-center font-display font-black text-2xl tracking-tight">
              VS
            </div>
          </div>
          <h1 className="font-display font-bold text-3xl tracking-tight text-[#2D2622]">
            Veneer Slate
          </h1>
          <div className="text-[11px] uppercase tracking-[0.3em] text-[#8B6F47] mt-2 font-semibold">
            Galaxy Impex · Stock Manager
          </div>
        </div>

        <div className="bg-white border border-[#E5D8C4] p-8 shadow-sm">
          {stage === "credentials" ? (
            <form onSubmit={sendOtp} className="space-y-5">
              <div>
                <label className="text-[11px] font-semibold text-[#8B6F47] uppercase tracking-[0.2em] mb-2 block">Email</label>
                <input data-testid="login-email" type="email" required autoFocus autoComplete="username"
                  value={email} onChange={(e) => setEmail(e.target.value)}
                  className="w-full h-12 px-4 bg-[#FAF7F2] border border-[#E5D8C4] focus:border-[#2D2622] focus:outline-none focus:ring-1 focus:ring-[#2D2622] text-sm" />
              </div>
              <div>
                <label className="text-[11px] font-semibold text-[#8B6F47] uppercase tracking-[0.2em] mb-2 block">Password</label>
                <input data-testid="login-password" type="password" required autoComplete="current-password"
                  value={password} onChange={(e) => setPassword(e.target.value)}
                  className="w-full h-12 px-4 bg-[#FAF7F2] border border-[#E5D8C4] focus:border-[#2D2622] focus:outline-none focus:ring-1 focus:ring-[#2D2622] text-sm" />
              </div>
              <button data-testid="login-submit" type="submit" disabled={loading}
                className="w-full h-12 bg-[#2D2622] text-[#FAF7F2] font-semibold text-xs uppercase tracking-[0.2em] hover:bg-[#5D4A2E] transition-colors flex items-center justify-center gap-2 disabled:opacity-60">
                {loading ? <Loader2 className="animate-spin" size={16} /> : <>Continue <ArrowRight size={16} /></>}
              </button>
              <div className="flex items-center gap-2 text-[11px] text-[#8B6F47] justify-center pt-2 border-t border-[#F3ECDF]">
                <Shield size={12} /> <span>Two-factor login · code sent via email</span>
              </div>
            </form>
          ) : (
            <form onSubmit={verify} className="space-y-5">
              <div className="text-center">
                <div className="text-[11px] uppercase tracking-[0.3em] text-[#8B6F47] font-semibold mb-2">Verify</div>
                <h2 className="font-display font-bold text-2xl text-[#2D2622]">Enter your code</h2>
                <p className="text-sm text-[#5D5148] mt-2">Sent to <span className="font-mono2 text-[#2D2622]">{email}</span></p>
              </div>
              <input data-testid="login-otp" required autoFocus inputMode="numeric" maxLength={6}
                value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                className="w-full h-16 px-4 bg-[#FAF7F2] border border-[#E5D8C4] focus:border-[#2D2622] focus:outline-none focus:ring-1 focus:ring-[#2D2622] font-mono2 text-3xl tracking-[0.5em] text-center" />
              <button data-testid="verify-submit" type="submit" disabled={loading || code.length !== 6}
                className="w-full h-12 bg-[#2D2622] text-[#FAF7F2] font-semibold text-xs uppercase tracking-[0.2em] hover:bg-[#5D4A2E] flex items-center justify-center gap-2 disabled:opacity-40">
                {loading ? <Loader2 className="animate-spin" size={16} /> : "Sign in"}
              </button>
              <div className="flex justify-between items-center pt-2 border-t border-[#F3ECDF]">
                <button type="button" onClick={() => setStage("credentials")} data-testid="back-btn"
                  className="text-[11px] uppercase tracking-widest text-[#8B6F47] hover:text-[#2D2622]">
                  ← Back
                </button>
                <button type="button" onClick={resend} disabled={cooldown > 0 || loading}
                  data-testid="resend-btn"
                  className="text-[11px] uppercase tracking-widest text-[#8B6F47] hover:text-[#2D2622] disabled:opacity-40">
                  {cooldown > 0 ? `Resend in ${cooldown}s` : "Resend code"}
                </button>
              </div>
            </form>
          )}
        </div>

        <div className="text-center mt-6 text-[10px] text-[#A8998A] uppercase tracking-[0.3em]">
          veneerslate.in
        </div>
      </div>
    </div>
  );
}
